import React, { useState } from 'react';
import { WidthProvider, Responsive } from 'react-grid-layout';
import { useParams } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import ChargingGraphHandler from '../fpm/handlers/ChargingGraphHandler';
import PlugPowerGraphHandler from '../fpm/handlers/PlugPowerGraphHandler';
import { ChargingCard } from '../fpm/cards/ChargingCard';

const ResponsiveGridLayout = WidthProvider(Responsive);

/**
 * GolfTrack Chargers dashboard. Same FPM backend / WS as the Power page so
 * data lines up across both views. Operator controls (per-plug on/off, all
 * off, all auto, plug commissioning) are intentionally NOT included here;
 * use the FPM app for those.
 */
const ChargingGrid: React.FC = () => {
  const { BuildingID } = useParams();
  const [showPowerOverTime, setShowPowerOverTime] = useState(false);

  const layouts = {
    lg: [
      { i: 'card', x: 0, y: 0, w: 12, h: 1, minW: 4, minH: 1 },
      { i: 'graph', x: 0, y: 1, w: 12, h: 5, minW: 6, minH: 5 },
    ],
    md: [
      { i: 'card', x: 0, y: 0, w: 12, h: 1, minW: 4, minH: 1 },
      { i: 'graph', x: 0, y: 1, w: 12, h: 5, minW: 4, minH: 5 },
    ],
    sm: [
      { i: 'card', x: 0, y: 0, w: 6, h: 1, minW: 2, minH: 1 },
      { i: 'graph', x: 0, y: 1, w: 6, h: 4, minW: 2, minH: 4 },
    ],
    xs: [
      { i: 'card', x: 0, y: 0, w: 2, h: 1, minW: 1, minH: 1 },
      { i: 'graph', x: 0, y: 1, w: 2, h: 4, minW: 1, minH: 4 },
    ],
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Box sx={{ minHeight: '100%', width: '100%' }}>
        <ResponsiveGridLayout
          className="layout"
          layouts={layouts}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
          cols={{ lg: 12, md: 12, sm: 6, xs: 2 }}
          rowHeight={100}
          margin={[10, 10]}
          compactType={null}
          draggableCancel=".cancelSelectorName"
          isDraggable={false}
          isResizable={false}
        >
          <div key="card" className="card-gauge">
            <ChargingCard BuildingID={BuildingID} />
          </div>
          <div key="graph" className="card-gauge">
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowPowerOverTime((prev) => !prev)}
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  zIndex: 2,
                  textTransform: 'none',
                  backgroundColor: 'background.paper',
                }}
              >
                {showPowerOverTime ? 'Show Charger Status' : 'Show Charging Power Over Time'}
              </Button>
              {showPowerOverTime ? (
                <PlugPowerGraphHandler BuildingID={BuildingID} />
              ) : (
                <ChargingGraphHandler BuildingID={BuildingID} />
              )}
            </Box>
          </div>
        </ResponsiveGridLayout>
      </Box>
    </Box>
  );
};

export default ChargingGrid;
